/* Shared header interaction. The heavy renderer is local, optional and loaded after page content. */
(() => {
  if (window.__essenceLogoMounted) return;
  window.__essenceLogoMounted = true;
  const instances = new Map();
  const modules = new Map();
  const STEP = Math.PI / 60;

  function mount(link) {
    if (instances.has(link)) return;
    const stage = link.querySelector('.el-logo__stage');
    const canvas = link.querySelector('canvas');
    if (!stage || !canvas) return;
    const events = new AbortController();
    const on = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: events.signal });
    let scene, loading, disposed = false, visible = true, frame = 0, idle = 0;
    let pointer = null, suppressUntil = 0;
    let velocity = 0, lastFrame = 0;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const previousDescription = link.getAttribute('aria-description');

    function stopSpin() { velocity = 0; lastFrame = 0; delete link.dataset.logoSpinning; }
    function stopFrame() { cancelAnimationFrame(frame); frame = 0; stopSpin(); if (pointer) pointer.velocity = 0; }
    function draw() {
      if (frame || !scene || !visible || document.hidden || disposed) return;
      frame = requestAnimationFrame(tick);
    }
    function tick(now) {
      frame = 0;
      if (!scene || disposed || !visible || document.hidden) return;
      if (velocity) {
        // Integrate exponential friction in seconds so refresh rate does not change the feel.
        const elapsed = Math.min(Math.max((now - lastFrame) / 1000, 0), .05);
        const decay = Math.exp(-3 * elapsed);
        scene.rotate(0, velocity * (1 - decay) / 3, 0);
        velocity *= decay;
        lastFrame = now;
        if (Math.abs(velocity) < .015) stopSpin();
      }
      try { scene.render(); } catch (_) { fallback(); return; }
      if (velocity) draw();
    }
    function resize() {
      if (!scene) return;
      const bounds = stage.getBoundingClientRect();
      if (bounds.width && bounds.height) scene.resize(bounds.width, bounds.height, Math.min(devicePixelRatio || 1, 2));
      draw();
    }
    function restoreLink() {
      delete link.dataset.logoDragging;
      delete link.dataset.logoActive;
      if (previousDescription === null) link.removeAttribute('aria-description');
      else link.setAttribute('aria-description', previousDescription);
    }
    function fallback() {
      stopFrame(); pointer = null;
      const current = scene; scene = null;
      current?.dispose();
      link.dataset.logoState = 'fallback';
      restoreLink();
    }
    async function prepare() {
      if (disposed || scene || loading || link.dataset.logoState === 'fallback' || !visible || document.hidden) return;
      link.dataset.logoState = 'loading';
      loading = (async () => {
        try {
          const url = link.dataset.sceneUrl;
          if (!modules.has(url)) modules.set(url, import(url));
          const { createLogoScene } = await modules.get(url);
          const created = await createLogoScene(canvas, link.dataset.logoSvg);
          if (disposed || !link.isConnected) { created.dispose(); return; }
          scene = created;
          resize();
          scene.render();
          link.dataset.logoState = 'ready';
          link.setAttribute('aria-description', 'Drag left or right to spin around the vertical axis; release to coast to a stop. Left and right arrow keys rotate the logo. Home or Escape resets. Enter opens the home page.');
          // Loading and ordinary clicks keep the frontal pose. Only intentional
          // dragging or arrow keys rotate the logo, including under reduced motion.
        } catch (_) {
          if (!disposed) fallback();
        }
      })();
      await loading;
      loading = null;
    }
    function schedule() {
      if (disposed || scene || idle || document.readyState !== 'complete') return;
      if ('requestIdleCallback' in window) idle = requestIdleCallback(() => { idle = 0; prepare(); }, { timeout: 1800 });
      else idle = setTimeout(() => { idle = 0; prepare(); }, 250);
    }
    function endPointer(event) {
      if (!pointer || event.pointerId !== pointer.id) return;
      const activateTouch = event.type === 'pointerup' && event.pointerType === 'touch' && !pointer.dragged;
      if (pointer.dragged) suppressUntil = performance.now() + 500;
      if (event.type === 'pointerup' && pointer.dragged && !reducedMotion.matches && visible && !document.hidden) {
        const age = performance.now() - pointer.time;
        velocity = age < 100 ? pointer.velocity * Math.exp(-age / 80) : 0;
        if (Math.abs(velocity) >= .015) {
          lastFrame = performance.now();
          link.dataset.logoSpinning = '';
          draw();
        } else stopSpin();
      } else stopSpin();
      const id = pointer.id;
      pointer = null;
      delete link.dataset.logoDragging;
      if (link.hasPointerCapture(id)) link.releasePointerCapture(id);
      if (activateTouch) {
        // Mobile browsers may omit the compatibility click after a captured drag.
        // Activate this fresh tap once; ignore any compatibility click that follows.
        suppressUntil = performance.now() + 500;
        link.click();
      }
    }
    on(link, 'pointerenter', () => { delete link.dataset.logoHintHidden; prepare(); });
    on(link, 'focus', () => { delete link.dataset.logoHintHidden; prepare(); });
    on(link, 'dragstart', event => event.preventDefault());
    on(link, 'pointerdown', event => {
      // A new press is a new activation, including modified clicks and fallback.
      suppressUntil = 0;
      if (!scene || !event.isPrimary || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey) return;
      stopSpin();
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY, dragged: false, time: performance.now(), velocity: 0 };
      link.setPointerCapture(event.pointerId);
    });
    on(link, 'pointermove', event => {
      if (!pointer || !scene || event.pointerId !== pointer.id) return;
      if (!pointer.dragged && Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) < 6) return;
      pointer.dragged = true;
      link.dataset.logoDragging = '';
      link.dataset.logoActive = '';
      const dx = event.clientX - pointer.x;
      const now = performance.now();
      pointer.velocity = Math.max(-12, Math.min(12, dx * .015 / Math.max((now - pointer.time) / 1000, .008)));
      pointer.time = now;
      scene.rotate(0, dx * .015, 0);
      pointer.x = event.clientX; pointer.y = event.clientY;
      event.preventDefault();
      draw();
    });
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) on(link, name, endPointer);
    on(link, 'click', event => {
      if (event.detail > 0 && performance.now() < suppressUntil) {
        suppressUntil = 0;
        event.preventDefault(); event.stopPropagation(); return;
      }
      // Every deliberate click keeps the anchor's native home navigation.
    }, { capture: true });
    on(link, 'keydown', event => {
      if (!scene || event.altKey || event.ctrlKey || event.metaKey) return;
      const turns = { ArrowLeft: [0, -STEP, 0], ArrowRight: [0, STEP, 0] };
      if (event.key === 'Escape' || event.key === 'Home') {
        stopSpin(); if (pointer) pointer.velocity = 0;
        event.preventDefault(); delete link.dataset.logoActive; scene.reset(); draw();
        if (event.key === 'Escape') link.dataset.logoHintHidden = '';
      }
      else if (turns[event.key]) {
        stopSpin(); if (pointer) pointer.velocity = 0;
        event.preventDefault();
        link.dataset.logoActive = '';
        scene.rotate(...turns[event.key]);
        draw();
      }
    });
    on(canvas, 'webglcontextlost', fallback);
    on(reducedMotion, 'change', () => { if (reducedMotion.matches) stopSpin(); });
    on(document, 'visibilitychange', () => {
      if (document.hidden) stopFrame();
      else { prepare(); resize(); }
    });
    const observer = new ResizeObserver(resize);
    observer.observe(stage);
    const visibility = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) { schedule(); draw(); } else stopFrame();
    });
    visibility.observe(link);
    const controls = {
      getRotation: () => scene?.getRotation() ?? null,
      reset: () => { stopSpin(); if (pointer) pointer.velocity = 0; delete link.dataset.logoActive; scene?.reset(); draw(); },
      destroy: () => {
        disposed = true;
        events.abort(); observer.disconnect(); visibility.disconnect(); stopFrame();
        if ('cancelIdleCallback' in window) cancelIdleCallback(idle); else clearTimeout(idle);
        scene?.dispose(); scene = null;
        restoreLink(); delete link.dataset.logoState;
        instances.delete(link); delete link.elLogo3D;
      },
      resume: () => { schedule(); resize(); },
      pause: stopFrame
    };
    link.elLogo3D = { getRotation: controls.getRotation, reset: controls.reset, destroy: controls.destroy };
    instances.set(link, controls);
    if (document.readyState === 'complete') schedule(); else on(window, 'load', schedule, { once: true });
  }
  function init(root = document) { if (root.matches?.('[data-el-logo]')) mount(root); root.querySelectorAll('[data-el-logo]').forEach(mount); }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { for (const [link, controls] of instances) if (event.target.contains(link)) controls.destroy(); });
  // Keep the last painted logo while the browser captures/leaves this document.
  // Document teardown releases WebGL; section unload still destroys explicitly.
  window.addEventListener('pagehide', () => { for (const controls of instances.values()) controls.pause(); });
  window.addEventListener('pageshow', event => { if (event.persisted) for (const controls of instances.values()) controls.resume(); });
})();
